import React, { useState, useEffect, useCallback } from "react";
import { PageHeader } from "./DashboardLayout";
import { api, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, DollarSign, Clock, CheckCircle2, XCircle, ExternalLink, RefreshCw, Globe, Youtube, Users, Calendar, Phone, AlignLeft, Link as LinkIcon } from "lucide-react";

export default function Monetize() {
    const [app, setApp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        website_url: "",
        youtube_url: "",
        social_urls: "",
        daily_audience: "",
        monthly_audience: "",
        yearly_audience: "",
        contact_no: "",
        description: "",
    });

    const loadApp = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/monetize/my-application");
            setApp(data && data.id ? data : null);
        } catch {
            setApp(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadApp(); }, [loadApp]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (form.description.length < 50) {
            toast.error("Description must be at least 50 characters.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/monetize/apply", {
                ...form,
                daily_audience: form.daily_audience ? parseInt(form.daily_audience) : null,
                monthly_audience: form.monthly_audience ? parseInt(form.monthly_audience) : null,
                yearly_audience: form.yearly_audience ? parseInt(form.yearly_audience) : null,
            });
            toast.success("Application submitted! We'll review it shortly.");
            setShowForm(false);
            await loadApp();
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const field = (key, val) => setForm(f => ({ ...f, [key]: val }));

    if (loading) return (
        <div className="flex items-center gap-3 p-10 text-ink-muted">
            <Loader2 className="animate-spin" size={20} /> Loading…
        </div>
    );

    const status = app?.status;

    return (
        <div>
            <PageHeader
                eyebrow="Monetize"
                title="Earn from your content."
                subtitle="Apply to monetize your Audo-powered audio content and unlock revenue sharing opportunities."
            />

            {/* Status: Approved */}
            {status === "approved" && (
                <div className="mb-8 bg-mint/20 border border-mint/40 rounded-3xl p-8 flex items-start gap-5">
                    <div className="w-12 h-12 rounded-2xl bg-mint flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={22} className="text-white" />
                    </div>
                    <div>
                        <h3 className="font-display text-[26px] font-semibold tracking-tight text-ink">You're monetized! 🎉</h3>
                        <p className="text-[15px] text-ink-muted mt-1">Your application was approved. Revenue sharing is now active on your content.</p>
                        <p className="text-[13px] text-ink-muted mt-3">Approved on {app.reviewed_at ? new Date(app.reviewed_at).toLocaleDateString("en-BD") : "—"}</p>
                    </div>
                </div>
            )}

            {/* Status: Pending */}
            {status === "pending" && (
                <div className="mb-8 bg-amber-50 border border-amber-200 rounded-3xl p-8 flex items-start gap-5">
                    <div className="w-12 h-12 rounded-2xl bg-amber-400 flex items-center justify-center flex-shrink-0">
                        <Clock size={22} className="text-white" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-display text-[26px] font-semibold tracking-tight text-amber-900">Application under review</h3>
                        <p className="text-[15px] text-amber-800 mt-1">We've received your application and are reviewing it. This usually takes 2–5 business days.</p>
                        <p className="text-[13px] text-amber-700 mt-3">Submitted on {app.created_at ? new Date(app.created_at).toLocaleDateString("en-BD") : "—"}</p>
                        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4 text-[13px]">
                            {app.website_url && <InfoChip icon={Globe} label="Website" value={app.website_url} link />}
                            {app.youtube_url && <InfoChip icon={Youtube} label="YouTube" value={app.youtube_url} link />}
                            {app.daily_audience && <InfoChip icon={Users} label="Daily audience" value={app.daily_audience.toLocaleString()} />}
                            {app.monthly_audience && <InfoChip icon={Calendar} label="Monthly audience" value={app.monthly_audience.toLocaleString()} />}
                            {app.contact_no && <InfoChip icon={Phone} label="Contact" value={app.contact_no} />}
                        </div>
                    </div>
                </div>
            )}

            {/* Status: Rejected */}
            {status === "rejected" && !showForm && (
                <div className="mb-8 space-y-5">
                    <div className="bg-red-50 border border-red-200 rounded-3xl p-8 flex items-start gap-5">
                        <div className="w-12 h-12 rounded-2xl bg-red-400 flex items-center justify-center flex-shrink-0">
                            <XCircle size={22} className="text-white" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-display text-[26px] font-semibold tracking-tight text-red-900">Application not approved</h3>
                            <p className="text-[15px] text-red-700 mt-1">Your previous application was reviewed and wasn't approved at this time. You can update your details and reapply.</p>
                            {app.rejection_reason && (
                                <div className="mt-4 bg-white border border-red-200 rounded-2xl p-4">
                                    <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-red-500 mb-1">Reason from admin</p>
                                    <p className="text-[14px] text-ink">{app.rejection_reason}</p>
                                </div>
                            )}
                            <button
                                onClick={() => {
                                    setForm({
                                        website_url: app.website_url || "",
                                        youtube_url: app.youtube_url || "",
                                        social_urls: app.social_urls || "",
                                        daily_audience: app.daily_audience ? String(app.daily_audience) : "",
                                        monthly_audience: app.monthly_audience ? String(app.monthly_audience) : "",
                                        yearly_audience: app.yearly_audience ? String(app.yearly_audience) : "",
                                        contact_no: app.contact_no || "",
                                        description: app.description || "",
                                    });
                                    setShowForm(true);
                                }}
                                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-ink hover:bg-coral text-cream-100 rounded-full text-[13px] font-semibold transition"
                            >
                                <RefreshCw size={13} /> Fix and reapply
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* No application or show form */}
            {(!app || showForm) && (
                <>
                    {/* Hero promo section */}
                    {!showForm && (
                        <div className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
                            <div className="lg:col-span-2 bg-ink text-cream-100 rounded-3xl p-8 relative overflow-hidden">
                                <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage: "radial-gradient(circle at 70% 50%, #FF5B3E 0%, transparent 60%)"}}/>
                                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-coral mb-4">Revenue sharing</p>
                                <h3 className="font-display text-[40px] sm:text-[52px] font-semibold tracking-tight leading-[1.05]">
                                    Your voice.<br/>Your <span className="text-coral">earnings.</span>
                                </h3>
                                <p className="mt-4 text-[15px] text-cream-100/70 max-w-md">
                                    Once approved, Audo shares ad revenue from your audio content. The bigger your audience, the more you earn. Apply today and start turning your articles into income.
                                </p>
                                <button
                                    onClick={() => setShowForm(true)}
                                    className="mt-7 inline-flex items-center gap-2 px-6 py-3 bg-coral hover:bg-white hover:text-ink text-white rounded-full text-[14px] font-semibold transition"
                                >
                                    Apply for monetization →
                                </button>
                            </div>
                            <div className="space-y-4">
                                {[
                                    { icon: DollarSign, title: "Ad revenue share", desc: "Earn from every play on your monetized content." },
                                    { icon: Users, title: "Audience growth", desc: "Insights + placement boost once you're in the program." },
                                    { icon: CheckCircle2, title: "Simple approval", desc: "Apply once. We review in 2–5 business days." },
                                ].map(({ icon: Icon, title, desc }) => (
                                    <div key={title} className="bg-white rounded-2xl border border-cream-300 p-5 flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-xl bg-coral/10 flex items-center justify-center flex-shrink-0">
                                            <Icon size={14} className="text-coral" />
                                        </div>
                                        <div>
                                            <p className="text-[13px] font-semibold text-ink">{title}</p>
                                            <p className="text-[12px] text-ink-muted">{desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Application Form */}
                    <div className="bg-white rounded-3xl border border-cream-300 shadow-soft p-8">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">Monetization application</p>
                                <h3 className="font-display text-[28px] font-semibold tracking-tight mt-1">Tell us about your platform</h3>
                            </div>
                            {showForm && (
                                <button onClick={() => setShowForm(false)} className="text-[13px] text-ink-muted hover:text-ink">Cancel</button>
                            )}
                        </div>
                        <form onSubmit={onSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <FormField icon={Globe} label="Website URL" placeholder="https://yoursite.com">
                                    <input type="url" value={form.website_url} onChange={e => field("website_url", e.target.value)}
                                        placeholder="https://yoursite.com"
                                        className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 pl-10 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition" />
                                </FormField>
                                <FormField icon={Youtube} label="YouTube channel URL" placeholder="https://youtube.com/c/...">
                                    <input type="url" value={form.youtube_url} onChange={e => field("youtube_url", e.target.value)}
                                        placeholder="https://youtube.com/c/..."
                                        className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 pl-10 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition" />
                                </FormField>
                                <FormField icon={LinkIcon} label="Other social media links" placeholder="Facebook, Instagram, X — comma separated" className="sm:col-span-2">
                                    <input type="text" value={form.social_urls} onChange={e => field("social_urls", e.target.value)}
                                        placeholder="https://facebook.com/page, https://x.com/handle"
                                        className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 pl-10 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition" />
                                </FormField>
                            </div>

                            <div>
                                <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-3">Audience reach</p>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    {[
                                        { key: "daily_audience", label: "Daily unique visitors" },
                                        { key: "monthly_audience", label: "Monthly unique visitors" },
                                        { key: "yearly_audience", label: "Yearly unique visitors" },
                                    ].map(({ key, label }) => (
                                        <div key={key} className="relative">
                                            <label className="block text-[12px] font-medium text-ink-muted mb-1.5">{label}</label>
                                            <input
                                                type="number" min="0" value={form[key]}
                                                onChange={e => field(key, e.target.value)}
                                                placeholder="0"
                                                className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <FormField icon={Phone} label="Contact number *" required>
                                <input type="tel" required value={form.contact_no} onChange={e => field("contact_no", e.target.value)}
                                    placeholder="+880 1XXXXXXXXX"
                                    className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 pl-10 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition" />
                            </FormField>

                            <div>
                                <label className="flex items-center gap-2 text-[12px] font-semibold text-ink mb-2">
                                    <AlignLeft size={13} className="text-coral" />
                                    Why do you qualify for monetization? *
                                    <span className="ml-auto text-ink-muted font-normal">{form.description.length}/3000</span>
                                </label>
                                <textarea
                                    required rows={6} value={form.description}
                                    onChange={e => field("description", e.target.value)}
                                    placeholder="Describe your platform, content type, audience demographics, and why you think Audo's monetization program is a good fit for you. Minimum 50 characters."
                                    className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition resize-none"
                                />
                                {form.description.length > 0 && form.description.length < 50 && (
                                    <p className="text-[12px] text-red-500 mt-1">{50 - form.description.length} more characters needed.</p>
                                )}
                            </div>

                            <button
                                type="submit" disabled={submitting}
                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-ink hover:bg-coral text-cream-100 rounded-full text-[14px] font-semibold transition disabled:opacity-60"
                            >
                                {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : <>Submit application →</>}
                            </button>
                        </form>
                    </div>
                </>
            )}
        </div>
    );
}

function FormField({ icon: Icon, label, children, required, className = "" }) {
    return (
        <div className={`relative ${className}`}>
            <label className="block text-[12px] font-semibold text-ink mb-1.5">
                {label}{required && <span className="text-coral ml-0.5">*</span>}
            </label>
            <div className="relative">
                {Icon && <Icon size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />}
                {children}
            </div>
        </div>
    );
}

function InfoChip({ icon: Icon, label, value, link }) {
    return (
        <div className="bg-white border border-amber-200 rounded-xl px-3 py-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-amber-600 mb-0.5">
                <Icon size={10} /> {label}
            </div>
            {link ? (
                <a href={value} target="_blank" rel="noreferrer" className="text-[13px] text-ink hover:text-coral truncate flex items-center gap-1">
                    <span className="truncate">{value}</span> <ExternalLink size={10} />
                </a>
            ) : (
                <p className="text-[13px] text-ink font-medium truncate">{value}</p>
            )}
        </div>
    );
}
